import { Injectable } from '@angular/core';
import { init, Data } from 'emoji-mart';
import * as localForage from 'localforage';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class EmojisService {
  public customEmojis: any[];

  private defaultEmojis: any[];

  private customEmojisVersion: number;

  private isLoaded = false;

  private db: any;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
  ) {
    this.db = localForage.createInstance({
      name: 'unthreadEmojis',
    });
  }

  async loadDefaultEmojis() {
    this.defaultEmojis = await this.db.getItem('defaultEmojis');

    if (!this.defaultEmojis) {
      const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data');

      this.defaultEmojis = await response.json();

      await this.db.setItem('defaultEmojis', this.defaultEmojis);
    }
  }

  async loadCustomEmojis() {
    this.customEmojisVersion = await this.db.getItem('customEmojisVersion');
    this.customEmojis = await this.db.getItem('customEmojis');

    if (
      !this.customEmojis ||
      !this.customEmojisVersion ||
      this.customEmojisVersion !== (this.authService.tenant.customEmojisVersion ?? 1)
    ) {
      const customEmojisConfig: {
        version: number;
        emojis: Record<string, string>;
      } = await this.apiService.getPromise('/me/tenant/custom-emojis');

      this.customEmojis = Object.entries(customEmojisConfig.emojis)
        .filter(([key, value]) => value.startsWith('https://'))
        .map(([key, value]) => ({
          id: key,
          name: key,
          keywords: ['custom'],
          skins: [
            {
              src: value,
            },
          ],
        }));

      await this.db.setItem('customEmojis', this.customEmojis);
      await this.db.setItem('customEmojisVersion', customEmojisConfig.version);
    }
  }

  async loadEmojis() {
    if (this.isLoaded) {
      return;
    }

    await Promise.all([this.loadDefaultEmojis(), this.loadCustomEmojis()]);

    await init({
      data: async () => {
        return this.defaultEmojis;
      },
      custom: [
        {
          id: 'custom',
          name: 'Custom',
          emojis: this.customEmojis,
        },
      ],
    });

    this.isLoaded = true; // Set isLoaded here to prevent multiple loads
  }

  async getEmojiList() {
    await this.loadEmojis();
    return Data?.emojis;
  }

  getReactionEmoji(emojiName: string) {
    const [name, skinTone] = emojiName.split('::');
    const skinToneIndexStr = skinTone ? skinTone.replace('skin-tone-', '') : '0';
    const skinToneIndex = parseInt(skinToneIndexStr, 10);

    const emoji = Data.emojis[name]?.skins?.[skinToneIndex];

    return emoji?.native ?? emoji?.src ?? `:${name}:`;
  }

  public async resolveEmojiToDisplay(emojiId: string): Promise<string> {
    if (!emojiId || typeof emojiId !== 'string') {
      return ''; // Return empty or a default if emojiId is not a string
    }

    await this.loadEmojis(); // Ensure emojis are loaded before proceeding

    // Attempt to get the emoji using the service's method
    let resolvedRepresentation = this.getReactionEmoji(emojiId);

    // Check if it's a URL (likely a custom emoji's src directly from getReactionEmoji)
    if (resolvedRepresentation && !resolvedRepresentation.startsWith(':') && resolvedRepresentation.includes('/')) {
      return `<img src="${resolvedRepresentation}" alt="${emojiId}" style="width: 1em; height: 1em; vertical-align: -0.125em; margin-right: 0.075em;" />`;
    }
    // Check if it's a native emoji (doesn't start with ':' and is short - heuristic)
    else if (resolvedRepresentation && !resolvedRepresentation.startsWith(':') && resolvedRepresentation.length <= 2) {
      return resolvedRepresentation; // Native emoji
    }
    // Fallback: If getReactionEmoji returned a colon string or something unexpected,
    // explicitly check customEmojis.
    else {
      const customEmoji = this.customEmojis?.find((e) => e.id === emojiId);
      if (customEmoji && customEmoji.skins?.[0]?.src) {
        return `<img src="${customEmoji.skins[0].src}" alt="${emojiId}" style="width: 1em; height: 1em; vertical-align: -0.125em; margin-right: 0.075em;" />`;
      }
      // If still not found, or if emojiId was not a string initially (e.g. already a native emoji from token map)
      // return the original colon string OR the original emojiId if it wasn't a colon string
      return emojiId.startsWith(':') && emojiId.endsWith(':') ? emojiId : emojiId;
    }
  }

  public getIsLoaded(): boolean {
    return this.isLoaded;
  }
}
